// Step definitions for the first-time HUD walkthrough, keyed by HUD type
// (`lessonMeta.layout`). Each step points at a pane that already exists in the
// corresponding layout component, so the layouts themselves need no markup
// changes. `placement` is the preferred side for the coach-mark card; the
// HudWalkthrough component falls back to whichever side has room.
//
//   canvas    → CanvasLayout
//   promptLab → PromptLabLayout (default layout)
//   ideaCard  → PromptLabLayout with `captureProject` (the app-idea lesson)
//   editor    → EditorLayout
//   webLab    → WebLabLayout
//   hotspot   → HotspotLayout
//   checklist → ChecklistLayout (reuses CanvasLayout's pane classes)
//   template  → TemplateStudioLayout
//
// Copy lives in the translation bundles (common.json → hudWt.*). Callers pass the
// i18n `t` so the tour localizes with the rest of the lesson UI; memoize the
// result on the active language so the array identity is stable between renders.

export const buildHudWalkthroughSteps = (t) => ({
    canvas: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.canvas.s1Title"),
            body: t("hudWt.canvas.s1Body"),
            placement: "right",
        },
        {
            selector: ".canvas-layout-canvas",
            title: t("hudWt.canvas.s2Title"),
            body: t("hudWt.canvas.s2Body"),
            placement: "left",
        },
        {
            selector: ".canvas-layout-chat",
            title: t("hudWt.canvas.s3Title"),
            body: t("hudWt.canvas.s3Body"),
            placement: "left",
        },
    ],

    promptLab: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.promptLab.s1Title"),
            body: t("hudWt.promptLab.s1Body"),
            placement: "right",
        },
        {
            selector: ".prompt-lab-pane--chat",
            title: t("hudWt.promptLab.s2Title"),
            body: t("hudWt.promptLab.s2Body"),
            placement: "left",
        },
        {
            selector: ".prompt-lab-pane--right",
            title: t("hudWt.promptLab.s3Title"),
            body: t("hudWt.promptLab.s3Body"),
            placement: "left",
        },
    ],

    // The app-idea lesson (captureProject) — PromptLabLayout rearranged: the card
    // is the exercise and takes the middle column, the chat drops under the rubric.
    // Keyed "ideaCard" rather than "promptLab" so the tour the student is shown
    // matches the panes actually in front of them.
    ideaCard: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.ideaCard.s1Title"),
            body: t("hudWt.ideaCard.s1Body"),
            placement: "right",
        },
        {
            selector: ".prompt-lab-pane--idea",
            title: t("hudWt.ideaCard.s2Title"),
            body: t("hudWt.ideaCard.s2Body"),
            placement: "left",
        },
        {
            selector: ".prompt-lab-right-top",
            title: t("hudWt.ideaCard.s3Title"),
            body: t("hudWt.ideaCard.s3Body"),
            placement: "left",
        },
        {
            selector: ".prompt-lab-right-bottom--chat",
            title: t("hudWt.ideaCard.s4Title"),
            body: t("hudWt.ideaCard.s4Body"),
            placement: "left",
        },
    ],

    webLab: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.webLab.s1Title"),
            body: t("hudWt.webLab.s1Body"),
            placement: "right",
        },
        {
            selector: ".prompt-lab-pane--chat",
            title: t("hudWt.webLab.s2Title"),
            body: t("hudWt.webLab.s2Body"),
            placement: "left",
        },
        {
            selector: ".web-lab-preview",
            title: t("hudWt.webLab.s3Title"),
            body: t("hudWt.webLab.s3Body"),
            placement: "left",
        },
        {
            selector: ".web-lab-checks",
            title: t("hudWt.webLab.s4Title"),
            body: t("hudWt.webLab.s4Body"),
            placement: "top",
        },
    ],

    checklist: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.checklist.s1Title"),
            body: t("hudWt.checklist.s1Body"),
            placement: "right",
        },
        {
            selector: ".canvas-layout-canvas",
            title: t("hudWt.checklist.s2Title"),
            body: t("hudWt.checklist.s2Body"),
            placement: "left",
        },
        {
            selector: ".canvas-layout-chat",
            title: t("hudWt.checklist.s3Title"),
            body: t("hudWt.checklist.s3Body"),
            placement: "left",
        },
    ],

    hotspot: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.hotspot.s1Title"),
            body: t("hudWt.hotspot.s1Body"),
            placement: "right",
        },
        {
            selector: ".hotspot-prompt",
            title: t("hudWt.hotspot.s2Title"),
            body: t("hudWt.hotspot.s2Body"),
            placement: "bottom",
        },
        {
            selector: ".hotspot-screen",
            title: t("hudWt.hotspot.s3Title"),
            body: t("hudWt.hotspot.s3Body"),
            placement: "left",
        },
    ],

    template: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.template.s1Title"),
            body: t("hudWt.template.s1Body"),
            placement: "right",
        },
        {
            selector: ".template-studio-form",
            title: t("hudWt.template.s2Title"),
            body: t("hudWt.template.s2Body"),
            placement: "left",
        },
        {
            selector: ".template-studio-preview",
            title: t("hudWt.template.s3Title"),
            body: t("hudWt.template.s3Body"),
            placement: "left",
        },
    ],

    editor: [
        {
            selector: ".prompt-lab-pane--lesson",
            title: t("hudWt.editor.s1Title"),
            body: t("hudWt.editor.s1Body"),
            placement: "right",
        },
        {
            selector: ".prompt-lab-pane--chat",
            title: t("hudWt.editor.s2Title"),
            body: t("hudWt.editor.s2Body"),
            placement: "left",
        },
        {
            selector: ".prompt-lab-pane--right",
            title: t("hudWt.editor.s3Title"),
            body: t("hudWt.editor.s3Body"),
            placement: "left",
        },
        {
            selector: ".editor-ask-claude",
            title: t("hudWt.editor.s4Title"),
            body: t("hudWt.editor.s4Body"),
            placement: "top",
        },
    ],
});

export default buildHudWalkthroughSteps;
